import {
  toRepliconDate,
  mapIsoStringtoUtcObject
} from '~/modules/common/dates/convert';
import { convertDurationToHours } from '~/modules/common/util';
import { OVERVIEW_UNITS_ENUM } from '~/modules/resource-management/common/enums';
import {
  convertHoursToFTE,
  convertHoursToPercentage
} from '~/modules/resource-management/ResourcingOverview/components/ResourceAllocationChart/hooks/fte-conversion';

export const DECIMAL_PLACES = 2;

export const CHART_KEYS = {
  BENCH: 'bench',
  NOT_WORKING: 'notWorking',
  FITTING_ALLOCATION: 'fittingAllocation',
  OVER_ALLOCATION: 'overAllocation',
  TOTAL_RESOURCES: 'totalResources',
  WORKING_TIME: 'workingTime',
  TARGET_UTILIZATION: 'targetUtilization'
};

const unitConverters = {
  [OVERVIEW_UNITS_ENUM.FTE]: convertHoursToFTE,
  [OVERVIEW_UNITS_ENUM.PERCENTAGE]: convertHoursToPercentage,
  [OVERVIEW_UNITS_ENUM.HOURS]: ({ hours }) => hours
};

const formatNumber = (value, decimals = DECIMAL_PLACES) =>
  Number((value || 0).toFixed(decimals));

const convertDataInDesiredUnits = ({
  chartHoursSummaryData,
  endDate,
  startDate,
  overviewUnit
}) => {
  const convertToUnit = unitConverters[overviewUnit];

  return Object.keys(chartHoursSummaryData).reduce((acc, key) => {
    acc[key] = formatNumber(
      convertToUnit({
        startDate,
        endDate,
        hours: chartHoursSummaryData[key],
        totalHours: chartHoursSummaryData[CHART_KEYS.TOTAL_RESOURCES]
      })
    );

    return acc;
  }, {});
};

const getFullPathLabel = (group, defaultLabel) => {
  if (!group) {
    return [defaultLabel];
  }

  const { fullPath, displayText } = group;

  return fullPath?.length
    ? [fullPath.join(' -> '), displayText]
    : [displayText];
};

export const mapToChartData = ({
  availabilityItem,
  groupBy,
  overviewUnit,
  defaultGroupDropdownLabel
}) => {
  const period = availabilityItem.periods?.[0];
  const label = getFullPathLabel(
    availabilityItem[groupBy],
    defaultGroupDropdownLabel
  );
  const rowId = availabilityItem.id || label[0];

  if (!period) {
    return Object.values(CHART_KEYS).reduce(
      (acc, key) => {
        acc[key] = 0;

        return acc;
      },
      {
        id: rowId,
        label,
        [groupBy]: availabilityItem[groupBy]
      }
    );
  }

  const {
    allocatedDuration,
    dateRange,
    holidayDuration,
    overAllocatedDuration,
    scheduledDuration,
    timeOffDuration
  } = period;

  const startDateInRepliconFormat = toRepliconDate(
    mapIsoStringtoUtcObject(dateRange.startDate)
  );
  const endDateInRepliconFormat = toRepliconDate(
    mapIsoStringtoUtcObject(dateRange.endDate)
  );

  const overAllocatedHours = convertDurationToHours(overAllocatedDuration);
  const scheduledHours = convertDurationToHours(scheduledDuration);
  const notWorkingHours =
    convertDurationToHours(timeOffDuration) +
    convertDurationToHours(holidayDuration);
  const workingTime = scheduledHours - notWorkingHours;
  const fittingAllocationHours =
    convertDurationToHours(allocatedDuration) - overAllocatedHours;
  const availableHours = workingTime - fittingAllocationHours;

  const chartHoursSummaryData = {
    [CHART_KEYS.BENCH]: availableHours,
    [CHART_KEYS.NOT_WORKING]: notWorkingHours,
    [CHART_KEYS.FITTING_ALLOCATION]: fittingAllocationHours,
    [CHART_KEYS.OVER_ALLOCATION]: overAllocatedHours,
    [CHART_KEYS.TOTAL_RESOURCES]: scheduledHours,
    [CHART_KEYS.WORKING_TIME]: workingTime,
    [CHART_KEYS.TARGET_UTILIZATION]: workingTime
  };

  return {
    id: rowId,
    label,
    [groupBy]: availabilityItem[groupBy],
    ...convertDataInDesiredUnits({
      chartHoursSummaryData,
      startDate: startDateInRepliconFormat,
      endDate: endDateInRepliconFormat,
      overviewUnit
    })
  };
};
